import { useEffect, useCallback } from 'react';
import { User } from 'firebase/auth';
import { UserProfile } from '@/lib/firebase/users';
import { AUTOSAVE_DEBOUNCE_MS } from '@/config/writing-editor-config';
import { upsertWritingTask } from '@/lib/firebase/writing';
import { useEssayCoreState } from './use-essay-core-state';
import { useEssayPlanState } from './use-essay-plan-state';
import { useEssayDerivedState } from './use-essay-derived-state';
import { useEssayScoreManager } from './use-essay-score-manager';
import { useEssaySlashCommandManager } from './use-essay-slash-command-manager';
import { useEssayTaskBank } from './use-essay-task-bank';
import { useEssayAutoSave } from './use-essay-auto-save';
import {
  EssayEditorStateProps,
  WritingSaveData,
  WritingTaskType,
} from '@/types/writing';

export function useEssayEditorState({
  initialPrompt = '',
  initialIntroduction = '',
  initialBodyParagraphs = [
    { type: '', content: '' },
    { type: '', content: '' },
  ],
  initialConclusion = '',
  initialEssayPlan,
  initialEssayId = null,
  onSuggestionSuccess,
  suggestionClearTrigger,
  user,
  userProfile,
  taskType = 'task2',
}: EssayEditorStateProps & { taskType?: WritingTaskType }) {
  const isProUser =
    userProfile?.role === 'pro' || userProfile?.role === 'admin';

  const fullFirebaseUser = (user === undefined ? null : user) as User | null;
  const fullUserProfile = (
    userProfile === undefined ? null : userProfile
  ) as UserProfile | null;

  const coreState = useEssayCoreState({
    initialPrompt,
    initialIntroduction,
    initialBodyParagraphs,
    initialConclusion,
    initialEssayId,
  });

  const planState = useEssayPlanState({
    initialEssayPlan,
    initialEssayId,
  });

  const derivedState = useEssayDerivedState({
    introduction: coreState.introduction,
    bodyParagraphs: coreState.bodyParagraphs,
    conclusion: coreState.conclusion,
  });

  const taskBankManager = useEssayTaskBank({
    initialPrompt: coreState.prompt,
    setPrompt: coreState.setPrompt,
  });

  const handleAddBodyParagraphAndPlan = useCallback(() => {
    coreState.addBodyParagraph();
  }, [coreState]);

  const handleRemoveBodyParagraphAndPlan = useCallback(
    (idx: number) => {
      coreState.removeBodyParagraph(idx);
    },
    [coreState]
  );

  const autoSaveManager = useEssayAutoSave({
    writingData: {
      taskType,
      prompt: coreState.prompt,
      introduction: coreState.introduction,
      bodyParagraphs: coreState.bodyParagraphs,
      conclusion: coreState.conclusion,
      essayPlan: planState.plan,
    } as WritingSaveData,
    user: fullFirebaseUser,
    initialEssayId,
    handleSave: async (data: WritingSaveData, id?: string | null) => {
      if (!fullFirebaseUser) throw new Error('User must be logged in to save.');
      return upsertWritingTask(fullFirebaseUser.uid, data, id);
    },
    debounceMs: AUTOSAVE_DEBOUNCE_MS,
  });

  const handleSuggestionSuccess = onSuggestionSuccess || (() => {});

  const scoreManager = useEssayScoreManager({
    userId: fullFirebaseUser?.uid,
    currentEssayId: autoSaveManager.currentEssayId,
    userProfile: fullUserProfile,
    isProUser,
    onScoreSuccess: handleSuggestionSuccess,
  });

  useEffect(() => {
    if (suggestionClearTrigger && suggestionClearTrigger > 0) {
      scoreManager.clearScore();
    }
  }, [suggestionClearTrigger, scoreManager]);

  const slashCommandManager = useEssaySlashCommandManager({
    introduction: coreState.introduction,
    bodyParagraphs: coreState.bodyParagraphs,
    conclusion: coreState.conclusion,
    essayPlan: planState.plan,
    prompt: coreState.prompt,
    user: fullFirebaseUser,
    selectedTask: taskBankManager.taskBank.selectedTask,
    taskType,
  });

  return {
    prompt: coreState.prompt,
    setPrompt: coreState.setPrompt,
    introduction: coreState.introduction,
    bodyParagraphs: coreState.bodyParagraphs,
    conclusion: coreState.conclusion,
    handleIntroductionChange: coreState.handleIntroductionChange,
    handleConclusionChange: coreState.handleConclusionChange,
    handlePromptChange: coreState.handlePromptChange,
    handleBodyParagraphChange: coreState.handleBodyParagraphChange,
    handleBodyTypeChange: coreState.handleBodyTypeChange,
    addBodyParagraph: handleAddBodyParagraphAndPlan,
    removeBodyParagraph: handleRemoveBodyParagraphAndPlan,

    essayPlan: planState.plan,
    setEssayPlan: planState.setPlan,
    handlePlanTypeChange: planState.handlePlanTypeChange,
    handlePlanContentChange: planState.handlePlanContentChange,
    addPlanItem: planState.addPlanItem,
    removePlanItem: planState.removePlanItem,

    isPlanOpen: planState.isPlanOpen,
    setIsPlanOpen: planState.setIsPlanOpen,

    isSlashMenuOpen: slashCommandManager.isSlashMenuOpen,
    slashMenuTargetElement: slashCommandManager.slashMenuTargetElement,
    slashMenuFilteredCommands: slashCommandManager.slashMenuFilteredCommands,
    slashMenuSelectedIndex: slashCommandManager.slashMenuSelectedIndex,
    setSlashMenuSelectedIndex: slashCommandManager.setSlashMenuSelectedIndex,
    handleSlashClose: slashCommandManager.handleSlashClose,
    handleSlashCommandTextareaKeyDown:
      slashCommandManager.handleSlashCommandTextareaKeyDown,
    handleSlashTrigger: slashCommandManager.handleSlashTrigger,
    handleSlashFilterUpdate: slashCommandManager.handleSlashFilterUpdate,
    executeSlashCommand: slashCommandManager.executeSlashCommand,
    isCommandResultPopoverOpen: slashCommandManager.isCommandResultPopoverOpen,
    isCommandLoading: slashCommandManager.isCommandLoading,
    commandResult: slashCommandManager.commandResult,
    commandError: slashCommandManager.commandError,
    commandResultTargetElement: slashCommandManager.commandResultTargetElement,
    closeCommandResultPopover: slashCommandManager.closeCommandResultPopover,
    activeCommandId: slashCommandManager.activeCommandId,
    slashMenuTriggerContext: slashCommandManager.slashMenuTriggerContext,

    taskBank: taskBankManager.taskBank,

    saveStatus: autoSaveManager.saveStatus,
    currentEssayId: autoSaveManager.currentEssayId,
    user: fullFirebaseUser,
    isProUser,
    score: scoreManager.score,
    scoreError: scoreManager.scoreError,
    isScoring: scoreManager.isScoring,
    showMinimalChangeDialog: scoreManager.showMinimalChangeDialog,
    setShowMinimalChangeDialog: scoreManager.setShowMinimalChangeDialog,
    fetchNewScore: scoreManager.fetchScore,
    clearScore: scoreManager.clearScore,
    hasHistory: scoreManager.hasHistory,
    isCheckingHistory: scoreManager.isCheckingHistory,
    totalWordCount: derivedState.totalWordCount,
    currentContent: derivedState.currentContent,
    canScore: scoreManager.checkScoringRequirements,
    scoringRequirementsList: scoreManager.checkScoringRequirements(
      {
        type: 'essay',
        introduction: coreState.introduction,
        bodyParagraphs: coreState.bodyParagraphs,
        conclusion: coreState.conclusion,
      },
      taskType
    ),
  };
}
